import Vue from "vue";
import store from "@/store";
import type { ButtonState } from "@/components/shared/dimssa-button.vue";

type PauseStatus = "Paused" | "Running" | "Unknown";

type PauseActionResult = {
  snackColor: "success" | "error";
  snackText: string;
};

class PauseJobsService {
  state = Vue.observable({
    pauseButtonState: "ready" as ButtonState,
    pauseStatus: "Unknown" as PauseStatus,
    pauseTimeoutDialogVisible: false,
  });

  private pauseReminderTimer: ReturnType<typeof setTimeout> | null = null;
  private pauseTimerManagedByUser = false;
  private readonly pauseDurationMs = 2 * 60 * 1000;

  private getLocationGuid() {
    const rootState = store.state as any;
    return rootState.user?.location?.guid as string | undefined;
  }

  hidePauseTimeoutDialog() {
    this.state.pauseTimeoutDialogVisible = false;
  }

  private clearPauseReminderTimer() {
    if (!this.pauseReminderTimer) return;
    clearTimeout(this.pauseReminderTimer);
    this.pauseReminderTimer = null;
  }

  private startPauseReminderTimer() {
    if (!this.pauseTimerManagedByUser || this.state.pauseStatus !== "Paused") return;

    this.clearPauseReminderTimer();
    this.pauseReminderTimer = setTimeout(() => {
      if (this.state.pauseStatus === "Paused") {
        this.state.pauseTimeoutDialogVisible = true;
      }
    }, this.pauseDurationMs);
  }

  private stopPauseReminderFlow() {
    this.pauseTimerManagedByUser = false;
    this.hidePauseTimeoutDialog();
    this.clearPauseReminderTimer();
  }

  async getPauseStatus() {
    const guid = this.getLocationGuid();
    if (!guid) {
      this.stopPauseReminderFlow();
      this.state.pauseStatus = "Unknown";
      return this.state.pauseStatus;
    }

    try {
      const gql = `query($guid:String!){
        PauseJobs(guid:$guid)
      }`;
      const json = await store.dispatch("graphQl", {
        gql,
        variables: { guid },
      });

      this.state.pauseStatus = (json?.data?.PauseJobs as PauseStatus) ?? "Unknown";
      if (this.state.pauseStatus !== "Paused") {
        this.stopPauseReminderFlow();
      }
    } catch (err) {
      console.log("getPauseStatus -> error: " + err);
      this.state.pauseStatus = "Unknown";
    }

    return this.state.pauseStatus;
  }

  private async setPauseJobs(pause: boolean) {
    const guid = this.getLocationGuid();
    if (!guid) throw Error("No location selected");

    const gql = `mutation($guid:String!, $pause:Boolean!){
      SetPauseJobs(guid:$guid, pause:$pause)
    }`;
    const json = await store.dispatch("graphQl", {
      gql,
      variables: { guid, pause },
    });
    this.state.pauseStatus = (json?.data?.SetPauseJobs as PauseStatus) ?? "Unknown";

    return this.state.pauseStatus;
  }

  async togglePauseJobs(): Promise<PauseActionResult | null> {
    if (this.state.pauseButtonState === "busy") return null;

    this.state.pauseButtonState = "busy";
    try {
      if (this.state.pauseStatus === "Unknown") await this.getPauseStatus();

      const pause = this.state.pauseStatus !== "Paused";
      await this.setPauseJobs(pause);

      if (pause && this.state.pauseStatus === "Paused") {
        this.pauseTimerManagedByUser = true;
        this.hidePauseTimeoutDialog();
        this.startPauseReminderTimer();
      } else if (!pause || this.state.pauseStatus !== "Paused") {
        this.stopPauseReminderFlow();
      }

      return {
        snackColor: "success",
        snackText: pause ? "Jobs paused for 2 minutes" : "Jobs resumed",
      };
    } catch (err) {
      console.log("togglePauseJobs -> error: " + err);
      return {
        snackColor: "error",
        snackText: `Pause failed: ${err}`,
      };
    } finally {
      this.state.pauseButtonState = "ready";
    }
  }

  async continuePausing(): Promise<PauseActionResult | null> {
    if (this.state.pauseButtonState === "busy") return null;

    this.state.pauseButtonState = "busy";
    try {
      await this.setPauseJobs(true);
      this.pauseTimerManagedByUser = true;
      this.hidePauseTimeoutDialog();
      this.startPauseReminderTimer();

      return {
        snackColor: "success",
        snackText: "Pause extended for another 2 minutes",
      };
    } catch (err) {
      console.log("continuePausing -> error: " + err);
      return {
        snackColor: "error",
        snackText: `Pause failed: ${err}`,
      };
    } finally {
      this.state.pauseButtonState = "ready";
    }
  }

  async stopPausingFromDialog(): Promise<PauseActionResult | null> {
    if (this.state.pauseButtonState === "busy") return null;

    this.state.pauseButtonState = "busy";
    try {
      await this.setPauseJobs(false);
      this.stopPauseReminderFlow();

      return {
        snackColor: "success",
        snackText: "Jobs resumed",
      };
    } catch (err) {
      console.log("stopPausingFromDialog -> error: " + err);
      return {
        snackColor: "error",
        snackText: `Pause failed: ${err}`,
      };
    } finally {
      this.state.pauseButtonState = "ready";
    }
  }
}

const pauseJobsService = new PauseJobsService();

export { pauseJobsService };
export type { PauseStatus, PauseActionResult };
